/**
 * The planner's stop edits as ops — trip co-editing, client side
 * (docs/plans/TRIP_COEDITING_PLAN.md §4.8, Stage C).
 *
 * A planner used to save the whole itinerary it held, so its copy of a stop
 * could overwrite a co-editor's newer edit. With ops it sends only what the
 * person did ("move this stop to the afternoon") to POST /api/trips/:id/ops,
 * and the server applies each op through one applier
 * (server/services/tripOpsService.js).
 *
 * Two pure halves, one file, so they can't drift:
 *
 *   planActivityOps(prev, next)  — every itinerary edit goes through the
 *     planner's one mutation funnel; this compares the days before and after
 *     and returns the ops that say what changed, plus `next` with the order
 *     keys those ops chose. It is not a call-site convention, so no edit path
 *     can forget its op (the custom-stop editor re-adds a stop, the detailed
 *     view re-sorts a day, a day removal takes its stops with it).
 *
 *   replayEntries(days, entries) — what the planner shows is the server's
 *     copy with the ops it hasn't seen confirmed replayed on top. On every
 *     re-read the planner takes the server's days and replays its pending
 *     ops; that is the whole merge (§4.8). Replays are idempotent: an op the
 *     server already applied (the read raced the answer) replays to nothing.
 *
 * An entry is { op, replay }: `op` is exactly what goes on the wire; `replay`
 * is what the planner needs to re-apply it to a copy of the days (the whole
 * stop for an add, the client-shaped fields for a set).
 *
 * Stops are keyed by their row id (`activityId`); a stop added in ops mode
 * gets the id it was minted (`clientActivityId`, the server's own format) as
 * its activityId straight away, so an op can name it before any answer.
 * Days are keyed by `uid` (trips' trip_days.uid); a day added here gets a uid
 * minted here, which the whole-itinerary save stores (keepDayIdentity).
 */
import { keyBetween, isValidSortKey } from './sortKeys';
import { parseDurationToMinutes } from './durationParse';
import { rescheduleActivityToDate } from './activityUtilities';

// ── ids ─────────────────────────────────────────────────────────────────────

function randomHex(bytes) {
  const buf = new Uint8Array(bytes);
  const c = typeof window !== 'undefined' ? window.crypto : null;
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(buf);
  else for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
  return Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
}

/** A new op id (the server takes /^op_[A-Za-z0-9_-]{8,64}$/). */
export function mintOpId() {
  return `op_${Date.now().toString(36)}_${randomHex(8)}`;
}

/** A new day uid in the server's format (trip_days.uid). */
export function mintDayUid() {
  return `day_${randomHex(16)}`;
}

const DAY_UID_RE = /^day_[0-9a-f]{32}$/;

/** A stop's row id: its own, or the one minted for it here. */
export const stopKey = (a) => (a && (a.activityId || a.activity_id || a.clientActivityId)) || null;

/**
 * Days where every day has a uid and every stop an activityId: a new day
 * gets a minted uid, a new stop takes its minted clientActivityId as its
 * row id (the op stores it under that id). A second day holding a uid
 * already used (a day copied by spreading another) gets a fresh one.
 * Returns the input when nothing changed (the mutation funnel's no-op rule).
 */
export function withOpIdentity(days) {
  if (!Array.isArray(days)) return days;
  const uids = new Set();
  let changed = false;
  const next = days.map(day => {
    if (!day) return day;
    let d = day;
    if (typeof d.uid !== 'string' || !DAY_UID_RE.test(d.uid) || uids.has(d.uid)) {
      d = { ...d, uid: mintDayUid() };
      changed = true;
    }
    uids.add(d.uid);
    const acts = d.activities;
    if (Array.isArray(acts) && acts.some(a => a && !a.activityId && !a.activity_id && a.clientActivityId)) {
      d = { ...d, activities: acts.map(a => (a && !a.activityId && !a.activity_id && a.clientActivityId ? { ...a, activityId: a.clientActivityId } : a)) };
      changed = true;
    }
    return d;
  });
  return changed ? next : days;
}

// ── fields ──────────────────────────────────────────────────────────────────

// The server's CUSTOM_ACTIVITY_CATEGORIES (tripsService.js, migration 199):
// anything else (the round-tripped 'custom' placeholder) is no category.
const CUSTOM_CATEGORIES = new Set([
  'restaurant', 'attraction', 'activity', 'shopping',
  'transport', 'accommodation', 'free_time', 'other'
]);
const BLOCKS = new Set(['morning', 'afternoon', 'evening', 'all_day']);

const pick = (a, ...keys) => {
  for (const k of keys) if (a[k] !== undefined) return a[k];
  return undefined;
};
const text = (v) => (typeof v === 'string' ? (v.trim() || null) : null);

function wireTime(v) {
  if (typeof v !== 'string') return null;
  const m = v.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  return h > 23 || Number(m[2]) > 59 ? null : `${String(h).padStart(2, '0')}:${m[2]}`;
}

function wireCoords(v) {
  if (!v || v.lat == null || v.lng == null || v.lat === '' || v.lng === '') return null;
  const lat = Number(v.lat);
  const lng = Number(v.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}

/** Each op field: how to read it off a client stop, and which client keys hold it. */
const FIELDS = {
  scheduled_time: { read: a => wireTime(pick(a, 'scheduledTime', 'scheduled_time')), keys: ['scheduledTime', 'scheduled_time'] },
  time_block: { read: a => { const b = pick(a, 'timeBlock', 'time_block'); return BLOCKS.has(b) ? b : null; }, keys: ['timeBlock', 'time_block'] },
  duration: {
    read: a => { const d = a.duration; return d === undefined || d === null || d === '' ? null : parseDurationToMinutes(d, null); },
    keys: ['duration', 'durationHours', 'durationMinutes']
  },
  end_date: { read: a => pick(a, 'endDate', 'end_date') || null, keys: ['endDate', 'end_date'] },
  // No length, at an exact minute (docs/plans/ADD_NOW_PLAN.md phase 2).
  quick_stop: { read: a => !!pick(a, 'quickStop', 'quick_stop'), keys: ['quickStop', 'quick_stop'] },
  // 'here' (made by "I'm here") or null — a stop that becomes a plan clears it.
  added_via: { read: a => (pick(a, 'addedVia', 'added_via') === 'here' ? 'here' : null), keys: ['addedVia', 'added_via'] },
  // The walk stop this stop is inside (phase 3, utils/dayWalks), or null.
  in_walk: { read: a => pick(a, 'inWalk', 'in_walk_id') || null, keys: ['inWalk', 'in_walk_id'] },
  notes: { read: a => text(a.notes) || '', keys: ['notes'] },
  title: { read: a => text(a.title), keys: ['title', 'name'] },
  description: { read: a => text(a.description), keys: ['description'] },
  location: { read: a => text(a.location), keys: ['location'] },
  coordinates: { read: a => wireCoords(a.coordinates), keys: ['coordinates'] },
  category: { read: a => (CUSTOM_CATEGORIES.has(a.category) ? a.category : null), keys: ['category'] }
};

// One set op per group, so a refused field (a title the server won't store)
// never takes the schedule with it.
const SET_GROUPS = [
  ['scheduled_time', 'time_block', 'duration', 'quick_stop', 'added_via', 'end_date'],
  // Its own op: a walk the server can't find (removed meanwhile) refuses only
  // the move into it, never the stop's new time.
  ['in_walk'],
  ['notes'],
  ['title', 'description', 'location', 'coordinates', 'category']
];

const CONTENT_KEYS = ['placeId', 'place_id', 'eventId', 'event_id', 'tourId', 'experienceId', 'viatorProductCode', 'tiqetsProductCode', 'walkId'];

/** A stop with no place, event, tour or product of its own (the server stores its words). */
function isCustomStop(a) {
  if (CONTENT_KEYS.some(k => a[k])) return false;
  return true;
}
/** Whether this planner may send `field` for this stop (mirrors the applier's
 *  mayStoreOwnWords: a place stop, Google or spine, stores no words of its own). */
function sendsField(a, field) {
  if (field === 'title' || field === 'description') return isCustomStop(a);
  if (field === 'location' || field === 'coordinates' || field === 'category') return isCustomStop(a);
  return true;
}

const sameWire = (field, x, y) => {
  if (field === 'coordinates') return (!x && !y) || (!!x && !!y && x.lat === y.lat && x.lng === y.lng);
  return (x ?? null) === (y ?? null);
};

const dateMs = (d) => Date.parse(`${d}T00:00:00Z`);
/** How many days past its start a stop runs (0 for a single day). */
function spanOf(a) {
  const start = pick(a, 'scheduledDate', 'scheduled_date');
  const end = FIELDS.end_date.read(a);
  if (!start || !end) return 0;
  const n = Math.round((dateMs(end) - dateMs(start)) / 86400000);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** The client keys `field` lives in, as `next` holds them (for a replay patch). */
function patchFor(fields, prev, next) {
  const patch = {};
  fields.forEach(f => FIELDS[f].keys.forEach(k => {
    if (k in next || (prev && k in prev)) patch[k] = next[k];
  }));
  return patch;
}

/** A field as the server answered it (overwrote.their_value), as client keys. */
export function patchFromWire(field, value) {
  switch (field) {
    case 'scheduled_time': return { scheduledTime: value ?? null };
    case 'time_block': return { timeBlock: value ?? null };
    case 'duration': return { duration: value ?? null, durationHours: undefined, durationMinutes: undefined };
    case 'end_date': return { endDate: value ?? null };
    case 'quick_stop': return { quickStop: !!value };
    case 'added_via': return { addedVia: value === 'here' ? 'here' : null };
    case 'in_walk': return { inWalk: value || null };
    case 'notes': return { notes: value ?? '' };
    case 'title': return { title: value ?? null, name: value ?? null };
    default: return { [field]: value ?? null };
  }
}

/** The fields of `activity.add` for a new stop (the applier's opAdd reads these). */
const PARTNER_KEYS = ['affiliateUrl', 'affiliate_url', 'experience_url', 'bookingUrl', 'booking_url'];
export function addFields(a, dayUid, sortKey) {
  const f = {
    day_uid: dayUid,
    sort_key: sortKey,
    time_block: FIELDS.time_block.read(a),
    scheduled_time: FIELDS.scheduled_time.read(a),
    notes: text(a.notes),
    source_id: typeof a.id === 'string' ? a.id : (a.id != null ? String(a.id) : undefined),
    place_id: a.placeId || a.place_id || undefined,
    event_id: a.eventId || a.event_id || undefined,
    tour_id: a.tourId || undefined,
    experience_id: a.experienceId || undefined,
    viator_product_code: a.viatorProductCode || undefined,
    tiqets_product_code: a.tiqetsProductCode != null ? String(a.tiqetsProductCode) : undefined,
    walk_id: a.walkId || undefined,
    activity_type: a.activityType || a.activity_type || undefined,
    source: a.source || undefined,
    category: a.category || undefined,
    title: text(a.title) || text(a.name) || undefined,
    description: text(a.description) || undefined,
    location: text(a.location) || text(a.address) || undefined,
    coordinates: wireCoords(a.coordinates) || undefined,
    duration: a.duration === '' ? undefined : a.duration,
    quick_stop: FIELDS.quick_stop.read(a) || undefined,
    added_via: a.addedVia === 'here' ? 'here' : undefined,
    in_walk: FIELDS.in_walk.read(a) || undefined,
    // A walk you made: a walk stop with no curated walk behind it.
    own_walk: a.ownWalk ? true : undefined
  };
  // An explicit end (null = "Same day") wins; with none the server may
  // derive one for a multi-day event or product, as the save did.
  if ('endDate' in a || 'end_date' in a) f.end_date = FIELDS.end_date.read(a);
  const partner = PARTNER_KEYS.map(k => (typeof a[k] === 'string' ? a[k] : null));
  if (partner.some(Boolean)) f.partner_urls = partner;
  Object.keys(f).forEach(k => f[k] === undefined && delete f[k]);
  return f;
}

// ── order ───────────────────────────────────────────────────────────────────

const keyed = (a) => (isValidSortKey(a?.sortKey) ? a.sortKey : null);
function cmpKeyed(x, y) {
  const kx = keyed(x) || '';
  const ky = keyed(y) || '';
  if (kx !== ky) return kx < ky ? -1 : 1;
  const ix = stopKey(x) || '';
  const iy = stopKey(y) || '';
  return ix < iy ? -1 : ix > iy ? 1 : 0;
}

/**
 * Indexes of the longest run of stops whose keys already strictly increase
 * (kept as they are). Strictly by key alone: two stops sharing a key (old
 * data) can't both stay, or nothing would fit between them.
 */
function longestIncreasing(items) {
  // items: [{ i, a }] in array order, every a with a valid key.
  const tails = [];
  const prevOf = new Array(items.length).fill(-1);
  items.forEach((it, n) => {
    let lo = 0;
    let hi = tails.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (items[tails[mid]].a.sortKey < it.a.sortKey) lo = mid + 1;
      else hi = mid;
    }
    prevOf[n] = lo > 0 ? tails[lo - 1] : -1;
    tails[lo] = n;
  });
  const keep = new Set();
  let k = tails.length ? tails[tails.length - 1] : -1;
  while (k >= 0) { keep.add(items[k].i); k = prevOf[k]; }
  return keep;
}

/**
 * The day's stops with keys that follow their array order: stops that came
 * from elsewhere, are new, or now sit out of order get a key between their
 * neighbours; the longest run already in order keeps its keys. Returns the
 * stops (re-keyed copies where needed) and the set of re-keyed indexes.
 */
function keyDay(activities, keepable) {
  const candidates = [];
  activities.forEach((a, i) => { if (keepable(a) && keyed(a)) candidates.push({ i, a }); });
  const keep = longestIncreasing(candidates);
  const out = activities.slice();
  const rekeyed = new Set();
  let lo = null;
  for (let i = 0; i < out.length; i++) {
    if (keep.has(i)) { lo = out[i].sortKey; continue; }
    let hi = null;
    for (let j = i + 1; j < out.length; j++) if (keep.has(j)) { hi = out[j].sortKey; break; }
    const key = keyBetween(lo, hi);
    out[i] = { ...out[i], sortKey: key };
    rekeyed.add(i);
    lo = key;
  }
  return { activities: out, rekeyed };
}

// ── plan: before/after → ops ────────────────────────────────────────────────

function indexStops(days) {
  const map = new Map();
  (days || []).forEach((day, d) => (day?.activities || []).forEach((a, i) => {
    const k = stopKey(a);
    if (k && !map.has(k)) map.set(k, { a, dayUid: day.uid, d, i });
  }));
  return map;
}

/**
 * The ops that turn `prev` into `next`, and `next` with the sort keys they
 * chose (so the planner shows exactly what a replay of them gives).
 *
 * @param {Array} prev - days before the edit (every day with a uid)
 * @param {Array} next - days after it
 * @param {object} [opts]
 * @param {Function} [opts.mintOpId]
 * @returns {{ days: Array, entries: Array<{op, replay}> }}
 */
export function planActivityOps(prev, next, { mintOpId: mint = mintOpId } = {}) {
  const before = indexStops(prev);
  const entries = [];
  const removals = [];
  const sets = [];
  let changed = false;

  const days = (next || []).map(day => {
    const acts = day?.activities;
    if (!Array.isArray(acts) || acts.length === 0) return day;
    // A stop keeps its key only when it was on this day before.
    const { activities, rekeyed } = keyDay(acts, (a) => before.get(stopKey(a))?.dayUid === day.uid);
    activities.forEach((a, i) => {
      const k = stopKey(a);
      if (!k) return;
      const was = before.get(k);
      if (!was) {
        entries.push({
          op: { op_id: mint(), type: 'activity.add', target: k, fields: addFields(a, day.uid, a.sortKey) },
          replay: { kind: 'add', key: k, dayUid: day.uid, stop: a }
        });
        return;
      }
      const movedDay = was.dayUid !== day.uid;
      if (movedDay || rekeyed.has(i)) {
        const fields = { sort_key: a.sortKey };
        if (movedDay) {
          fields.day_uid = day.uid;
          fields.time_block = FIELDS.time_block.read(a);
        }
        entries.push({
          op: { op_id: mint(), type: 'activity.move', target: k, fields },
          replay: { kind: 'move', key: k, dayUid: day.uid, sortKey: a.sortKey, patch: movedDay ? patchFor(['time_block'], was.a, a) : {} }
        });
      }
      sets.push({ k, was: was.a, now: a, movedDay });
    });
    if (rekeyed.size === 0) return day;
    changed = true;
    return { ...day, activities };
  });

  const after = indexStops(days);
  before.forEach((was, k) => {
    if (!after.has(k)) {
      removals.push({
        op: { op_id: mint(), type: 'activity.remove', target: k },
        replay: { kind: 'remove', key: k, dayUid: was.dayUid, stop: was.a }
      });
    }
  });

  sets.forEach(({ k, was, now, movedDay }) => {
    SET_GROUPS.forEach(group => {
      const fields = {};
      const seen = {};
      group.forEach(f => {
        if (!sendsField(now, f)) return;
        // A move carries its block; the span follows the move (the server
        // keeps a multi-day stop's length) — only a changed length is an edit.
        if (f === 'time_block' && movedDay) return;
        if (f === 'end_date') {
          if (spanOf(was) === spanOf(now)) return;
        } else if (sameWire(f, FIELDS[f].read(was), FIELDS[f].read(now))) {
          return;
        }
        fields[f] = FIELDS[f].read(now);
        seen[f] = FIELDS[f].read(was);
      });
      const names = Object.keys(fields);
      if (names.length === 0) return;
      entries.push({
        op: { op_id: mint(), type: 'activity.set', target: k, fields, seen },
        replay: { kind: 'set', key: k, patch: patchFor(names, was, now) }
      });
    });
  });

  // A walk goes before the stops added inside it in the same batch (the
  // server finds the walk an add names only once it is stored).
  const isWalkAdd = (e) => e.op.type === 'activity.add' && (e.op.fields.activity_type === 'walk' || e.op.fields.own_walk || e.op.fields.walk_id);
  const ordered = [...entries.filter(isWalkAdd), ...entries.filter(e => !isWalkAdd(e))];
  return { days: changed ? days : next, entries: [...ordered, ...removals] };
}

/**
 * A stop removed and added back by two separate edits (an editor that
 * re-adds the stop it changed): the add can't go as an add — the server
 * would refuse it as removed by this very planner. When the removal is still
 * unsent the two cancel and the edit goes as a move and sets; once sent, as
 * a restore and sets. Returns the entries to send instead of the add.
 */
export function reAddAsEdit(removal, add, { removalSent, mintOpId: mint = mintOpId }) {
  const k = add.replay.key;
  const stop = add.replay.stop;
  const out = [];
  if (removalSent) {
    out.push({
      op: { op_id: mint(), type: 'activity.restore', target: k, fields: { day_uid: add.replay.dayUid, sort_key: stop.sortKey } },
      replay: { kind: 'add', key: k, dayUid: add.replay.dayUid, stop }
    });
  } else if (removal.replay.dayUid !== add.replay.dayUid || removal.replay.stop.sortKey !== stop.sortKey) {
    // Back where it was (the usual editor case): no move at all.
    const fields = { sort_key: stop.sortKey };
    if (removal.replay.dayUid !== add.replay.dayUid) {
      fields.day_uid = add.replay.dayUid;
      fields.time_block = FIELDS.time_block.read(stop);
    }
    out.push({
      op: { op_id: mint(), type: 'activity.move', target: k, fields },
      replay: { kind: 'move', key: k, dayUid: add.replay.dayUid, sortKey: stop.sortKey, patch: patchFor(['time_block'], removal.replay.stop, stop) }
    });
  }
  const was = removal.replay.stop;
  SET_GROUPS.forEach(group => {
    const fields = {};
    const seen = {};
    group.forEach(f => {
      if (!sendsField(stop, f)) return;
      if (f === 'end_date' ? spanOf(was) === spanOf(stop) : sameWire(f, FIELDS[f].read(was), FIELDS[f].read(stop))) return;
      fields[f] = FIELDS[f].read(stop);
      seen[f] = FIELDS[f].read(was);
    });
    const names = Object.keys(fields);
    if (names.length === 0) return;
    out.push({
      op: { op_id: mint(), type: 'activity.set', target: k, fields, seen },
      replay: { kind: 'set', key: k, patch: patchFor(names, was, stop) }
    });
  });
  return out;
}

// ── replay: server days + pending → shown ───────────────────────────────────

function findStop(days, key) {
  for (let d = 0; d < days.length; d++) {
    const acts = days[d]?.activities || [];
    for (let i = 0; i < acts.length; i++) if (stopKey(acts[i]) === key) return { d, i };
  }
  return null;
}

function insertByKey(activities, stop) {
  const out = activities.slice();
  let at = out.length;
  for (let i = 0; i < out.length; i++) {
    if (cmpKeyed(out[i], stop) > 0) { at = i; break; }
  }
  out.splice(at, 0, stop);
  return out;
}

const withDay = (days, d, activities) => {
  const out = days.slice();
  out[d] = { ...days[d], activities };
  return out;
};

/** One entry replayed onto `days`; the input back when it changes nothing. */
export function applyEntry(days, entry) {
  const r = entry.replay;
  const at = findStop(days, r.key);
  switch (r.kind) {
    case 'add': {
      if (at) return days;
      const d = days.findIndex(day => day?.uid === r.dayUid);
      if (d === -1) return days;
      const date = days[d].date;
      const stop = r.stop.scheduledDate && date && r.stop.scheduledDate !== date
        ? rescheduleActivityToDate(r.stop, date) : r.stop;
      return withDay(days, d, insertByKey(days[d].activities || [], stop));
    }
    case 'set': {
      if (!at) return days;
      const acts = days[at.d].activities.slice();
      acts[at.i] = { ...acts[at.i], ...r.patch };
      return withDay(days, at.d, acts);
    }
    case 'move': {
      if (!at) return days;
      const to = r.dayUid ? days.findIndex(day => day?.uid === r.dayUid) : at.d;
      if (to === -1) return days;
      let stop = { ...days[at.d].activities[at.i], ...r.patch, sortKey: r.sortKey };
      if (to !== at.d && days[to].date) stop = rescheduleActivityToDate(stop, days[to].date);
      let out = withDay(days, at.d, days[at.d].activities.filter((_, i) => i !== at.i));
      out = withDay(out, to, insertByKey(out[to].activities || [], stop));
      return out;
    }
    case 'remove': {
      if (!at) return days;
      return withDay(days, at.d, days[at.d].activities.filter((_, i) => i !== at.i));
    }
    default:
      return days;
  }
}

/** The server's days with these entries replayed on top, in order. */
export function replayEntries(days, entries) {
  let out = days || [];
  (entries || []).forEach(e => { out = applyEntry(out, e); });
  return out;
}

/**
 * Days without their stops: what the whole-itinerary save still carries
 * while stops go as ops (days, stays, transports until Stage D).
 */
export function daysWithoutStops(days) {
  return (days || []).map(({ activities, ...rest }) => rest); // eslint-disable-line no-unused-vars
}
